/**
 * Route guard, used as a layout route.
 *
 *     { element: <ProtectedRoute />, children: [...] }
 *     { element: <ProtectedRoute view="settings" />, children: [...] }
 *     { element: <ProtectedRoute staffOnly />, children: [...] }
 *
 * Being signed out redirects to the login screen. Being signed in but not
 * allowed renders a 403 page instead: bouncing the user somewhere else hides
 * *why* they cannot see the screen, and if the destination is also protected
 * it loops.
 *
 * `view` routes every gate through the registry in `@/config/views`: a view
 * there may declare `staffOnly`, `permission` and `anyRoles`, and `canSeeView`
 * consults `/auth/me/` — so a route declared with `view` needs no change when
 * the access model grows.
 */
import { Navigate, Outlet, useLocation } from 'react-router';
import type { ReactNode } from 'react';

import type { ViewKey } from '@/config/views';
import { Forbidden } from '@/shared/components/Forbidden';
import { FullPageLoader } from '@/shared/components/FullPageLoader';
import { useCanSee, useIsStaff } from '@/shared/hooks/usePermission';

import { useAuth } from '../hooks/useAuth';

interface Props {
  view?: ViewKey;
  staffOnly?: boolean | undefined;
}

export function ProtectedRoute({ view, staffOnly }: Props) {
  const { isAuthenticated, isRestoring } = useAuth();
  const location = useLocation();
  const canSee = useCanSee(view);
  const isStaff = useIsStaff();

  // Must wait: rendering the redirect before the session is restored would
  // bounce every authenticated user to the login screen on a page refresh.
  if (isRestoring) return <FullPageLoader />;

  if (!isAuthenticated) {
    // `state.from` lets the login screen return the user where they meant to go.
    return <Navigate to="/ingresar" replace state={{ from: location }} />;
  }

  if (view && !canSee) return <Forbidden />;
  if (staffOnly && !isStaff) return <Forbidden />;

  return <Outlet />;
}

/**
 * Leaf-page variant of the same gate: guards a single screen instead of a
 * route tree. Used where an `index` route cannot nest a `ProtectedRoute`
 * (index routes take no children).
 */
export function GuardedView({ view, children }: { view: ViewKey; children: ReactNode }) {
  const { isAuthenticated, isRestoring } = useAuth();
  const location = useLocation();
  const canSee = useCanSee(view);

  if (isRestoring) return <FullPageLoader />;
  if (!isAuthenticated) {
    return <Navigate to="/ingresar" replace state={{ from: location }} />;
  }
  if (!canSee) return <Forbidden />;

  return <>{children}</>;
}
